/* eslint-disable max-lines-per-function */
/* eslint-disable no-param-reassign */
/* eslint-disable default-case */
/* eslint-disable @typescript-eslint/no-unsafe-call */
// @ts-nocheck
import { type Layer, type HXKLineLayerSpec, defineHXKLineLayer } from '../../../core/layer';
import type { StockData, KLineData } from '../../../helper/hxKLine';
import type { KLineTooltipItem } from '../ui/kLineTooltip/KLineTooltip';
import { validateHXKlineComponents } from '../../../util/hxKLine';

/**
 * 股票信息类型
 */
type Stock = {
  id: string;
  code: string;
  market: string;
  name: string;
  data_class?: string;
  time_period?: string;
  trade_date?: number;
  adjust_type?: string;
  trade_class: string;
};

// 定义HXKline实例接口
interface HXKlineInstance {
  setZoomEnabled: (enabled: boolean) => void;
  setScrollEnabled: (enabled: boolean) => void;
  setBarSpaceLimit: (limit: { min: number; max: number }) => void;
  zoomToShowAllData: () => void;
  setStyles: (options: unknown) => void;
  getDom: (id: string, type: string) => HTMLElement;
  setPaneOptions: (options: {
    id: string;
    height?: number;
    dragEnabled?: boolean;
    gap?: { top: number; bottom: number };
    axisOptions?: { scrollZoomEnabled: boolean };
  }) => void;
  createIndicator: (
    options: {
      name: string;
      shouldFormatBigNumber: boolean;
      precision: number;
    },
    createNewPane: boolean,
    additionalOptions: Record<string, unknown>
  ) => string;
  overrideIndicator: (
    options: {
      name: string;
      shortName: string;
      calcParams: unknown[];
      precision: number;
      figures: {
        key: string;
        title: string;
        type: string;
        baseValue: number;
      }[];
    },
    id: string
  ) => void;
  subscribeAction: (
    action: string,
    callback: (data: {
      dataIndex: number;
      indicatorData: {
        [key: string]: {
          VOL: {
            volume: string;
          };
        };
      };
    }) => void
  ) => void;
}

// 确保HXKline和HXKlineChart已经被正确导入
declare const HXKline: {
  setHXKlineChart: (chart: unknown) => void;
  verifyProjectPermission: (info: unknown) => void;
  initChart: (
    dom: HTMLElement,
    stockInfo: Stock,
    options: {
      locale: string;
      customApi: {
        formatBigNumber: (value: number) => string | number;
      };
      styles: string;
    },
    callback: (status: string, ...args: unknown[]) => void
  ) => HXKlineInstance;
  disposeChart: (dom: HTMLElement, callback: (...args: unknown[]) => void) => void;
};

declare const HXKlineChart: unknown;

// 定义UI组件接口
interface UIComponents {
  ScrollButtonGroup: new (
    container: HTMLElement,
    options: {
      buttonArray: string[];
      data: Array<{ key: number; value: StockData }>;
      token: unknown;
    }
  ) => {
    on: (event: string, callback: (e: { value: StockData }) => void) => void;
    destroy?: () => void;
  };
  ButtonGroup: new (
    container: HTMLElement,
    options: {
      buttonArray: string[];
      data: Array<{ key: number; value: string }>;
      token: unknown;
    }
  ) => {
    on: (event: string, callback: (e: { key: number; value: string }) => void) => void;
    init: () => void;
    destroy?: () => void;
  };
  KLineTooltip: new (
    container: HTMLElement,
    options: { token: unknown }
  ) => {
    show: (data: KLineTooltipItem[]) => void;
    destroy?: () => void;
  };
}

// 定义API接口
interface ApiInterface {
  layout: string;
  hook: {
    globalConfig: (api: unknown) => { hxKLineVerifyInfo: unknown };
    getHXKLineTooltipData: (
      kline: unknown,
      type: 'kline' | 'trend',
      dataIndex?: number
    ) => {
      defaultTooltipData: KLineTooltipItem[];
      kLineData: KLineData;
    };
  };
  util: {
    common: {
      generateUID: () => string;
    };
    const: {
      LAYOUT: Record<string, string>;
      HXKLINE_COLOR: [string, string, string];
    };
  };
  ui: UIComponents;
  token: {
    dvHXKLine?: {
      constants?: {
        DEFAULT_TICK_LINE_COLOR?: string;
      };
      hxKLine?: {
        option?: unknown;
      };
      tooltip?: {
        text?: {
          color?: string;
        };
      };
    };
    dvScrollButtonGroup?: unknown;
    dvButtonGroup?: unknown;
  };
}
validateHXKlineComponents();
HXKline.setHXKlineChart(HXKlineChart);

/**
 * 组件基本信息配置
 */
const visInfo: Layer['visInfo'] = {
  id: 'hxKLine',
  name_en: 'Candlestick Chart',
  // name_zh: '行情图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  parameters: {},
  extra: {
    isNotTrainedByLLM: true
  }
};

/**
 * 组件配置接口
 */
export interface Spec extends HXKLineLayerSpec {
  stocks: StockData[];
}

/**
 * 股票类型常量
 */
const STOCK_TYPE = {
  TREND: 'trend',
  KLINE: 'kline'
};

/**
 * 根据布局获取tooltip显示的数据
 * @param tooltipArray tooltip数据数组
 * @param layout 布局类型
 * @param LAYOUT 布局常量
 * @returns 根据布局筛选后的tooltip数据
 */
function getTooltipData(
  tooltipArray: KLineTooltipItem[],
  layout: string,
  LAYOUT: Record<string, string>
): KLineTooltipItem[] {
  if (layout === LAYOUT.SMALL || layout === LAYOUT.SMALL_1_2) {
    // 设计师规定，卡片宽度较小时只显示两项tooltip字段
    return tooltipArray.slice(0, 3);
  }
  // 其他情况下展示全量字段
  return tooltipArray;
}

/**
 * 行情图组件实现
 */
export const layer = defineHXKLineLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { layout, hook: hooks, util: utils, ui, token } = api as ApiInterface;

    // 验证项目权限
    HXKline.verifyProjectPermission(hooks.globalConfig(api).hxKLineVerifyInfo);

    // 创建DOM元素
    const $tooltipContainer = document.createElement('div');
    const $scrollButtonGroup = document.createElement('section');
    const $ButtonGroup = document.createElement('section');
    const $indicatorTooltip = document.createElement('div');
    const $chart = document.createElement('section');

    // 样式配置
    const lineColor = token?.dvHXKLine?.constants?.DEFAULT_TICK_LINE_COLOR;
    const borderLineStyle = `1px solid ${lineColor || 'rgba(125, 133, 146, 0.3)'}`;

    // 设置DOM样式
    $dom.style.cssText +=
      'display: flex; flex-direction: column; justify-content: center; align-items: center;';
    $scrollButtonGroup.style.cssText = 'width: 100%; margin-bottom: 8px;';
    $ButtonGroup.style.cssText =
      'width: 100%; height: 22px; display: flex; margin-bottom: 8px; flex-direction: row;';
    $indicatorTooltip.style.cssText =
      'color: #60656C; font-size: 12px; font-weight: 400; line-height: 12px; margin-left: 4px; margin-top: 4px; font-family: Microsoft YaHei;';
    $chart.style.cssText = 'flex: 1; height: 0; width: 100%;';

    // 添加元素到DOM
    $dom.appendChild($scrollButtonGroup);
    $dom.appendChild($ButtonGroup);
    $dom.appendChild($tooltipContainer);
    $dom.appendChild($chart);

    // 数据初始化
    const { stocks } = spec;
    const stockIndex = 0;
    const buttonNameMap = {
      分时: 'trend',
      日线: 'day_1',
      周线: 'week_1',
      月线: 'month_1'
    };
    let stockType: string = Object.values(buttonNameMap)[stockIndex];

    /**
     * 生成股票信息对象
     * @returns 股票信息对象
     */
    const generateStockInfo = (): Stock => ({
      id: utils.common.generateUID(),
      code: stocks[stockIndex].code,
      market: stocks[stockIndex].market,
      name: stocks[stockIndex].name,
      data_class: STOCK_TYPE.TREND,
      trade_class: 'intraday',
      trade_date: 0
    });

    let stockInfo: Stock = generateStockInfo();

    // 初始化按钮组
    const scrollButtonGroupIns = new ui.ScrollButtonGroup($scrollButtonGroup, {
      buttonArray: stocks.map(stock => stock.name),
      data: stocks.map((value, key) => ({ key, value })),
      token: token.dvScrollButtonGroup
    });

    // 隐藏滚动按钮组（当只有一支股票时）
    if (stocks.length <= 1) {
      $scrollButtonGroup.style.cssText = 'display: none;';
    }

    // 初始化按钮组
    const buttonGroupIns = new ui.ButtonGroup($ButtonGroup, {
      buttonArray: Object.keys(buttonNameMap),
      data: Object.values(buttonNameMap).map((value, key) => ({ key, value })),
      token: token.dvButtonGroup
    });

    // 初始化提示组件
    const kLineTooltipIns = new ui.KLineTooltip($tooltipContainer, { token });

    // 图表实例引用
    let chartIns: HXKlineInstance | undefined;

    /**
     * 显示K线图的提示信息
     * @param kline K线图实例
     * @param dataIndex 数据索引
     * @returns K线数据
     */
    function showKLineTooltip(kline: unknown, dataIndex?: number): KLineData {
      // 获取k线的默认tooltip数据和k线原始数据
      const { defaultTooltipData: defaultKLineTooltip, kLineData } = hooks.getHXKLineTooltipData(
        kline,
        'kline',
        dataIndex
      );

      // 获取分时的默认tooltip数据
      const { defaultTooltipData: defaultTrendTooltip } = hooks.getHXKLineTooltipData(
        kline,
        'trend',
        dataIndex
      );

      // 根据卡片大小和行情图种类确定tooltip的展示数目
      const { LAYOUT } = utils.const;
      const showTooltip =
        stockType !== STOCK_TYPE.TREND
          ? getTooltipData(defaultKLineTooltip, layout, LAYOUT)
          : getTooltipData(defaultTrendTooltip, layout, LAYOUT);

      kLineTooltipIns.show(showTooltip);

      return kLineData;
    }

    /**
     * 初始化图表
     * @param dom 容器DOM元素
     * @param newStockInfo 股票信息
     * @returns K线图实例
     */
    function chartInit(dom: HTMLElement, newStockInfo: Stock): HXKlineInstance {
      // 销毁现有图表
      if (chartIns) {
        HXKline.disposeChart($chart, (...args) => {
          console.log('hxKLine destroy', ...args);
        });
      }

      // 大数字单位定义
      const UNIT = {
        万: 10000,
        亿: 100000000
      };

      /**
       * 格式化大数字
       * @param value 数值
       * @returns 格式化后的数值
       */
      const formatBigNumber = (value: number): string | number => {
        let result: string | number = Number(value).toFixed(2);
        Object.keys(UNIT).forEach(name => {
          if (value > +UNIT[name]) {
            result = `${(value / +UNIT[name]).toFixed(2)}${name}`;
          }
        });

        return result;
      };

      // 初始化K线图
      const kline = HXKline.initChart(
        dom,
        newStockInfo,
        /**
         * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/chart-api.html
         */
        {
          locale: 'zh_CN',
          customApi: {
            formatBigNumber
          },
          styles: 'customTheme'
        },
        (...args) => {
          if (args[0] === 'success') {
            // 设置图表行为
            kline.setZoomEnabled(false);
            kline.setScrollEnabled(false);
            kline.setBarSpaceLimit({ min: 0, max: 50 });
            kline.zoomToShowAllData();

            // 应用自定义样式
            if (token?.dvHXKLine?.hxKLine?.option) {
              kline.setStyles(token?.dvHXKLine?.hxKLine?.option);
            }

            // 处理DOM样式
            const $main = dom.querySelector('div > div > div:nth-child(1)') as HTMLElement;
            const $indicator = dom.querySelector(
              'div > div:nth-child(3) > div:nth-child(1)'
            ) as HTMLElement;
            const $indicatorXAxis = $chart.querySelector(
              'div > div:nth-child(4) > div:nth-child(1)'
            ) as HTMLElement;
            const $candleYAxis = $chart.querySelector(
              'div > div > div:nth-child(2)'
            ) as HTMLElement;
            const $indicatorYAxis = $chart.querySelector(
              'div > div:nth-child(3) > div:nth-child(2)'
            ) as HTMLElement;

            // 禁用坐标轴拖动
            if ($indicatorXAxis) {
              $indicatorXAxis.style.cursor = 'default';
            }
            if ($candleYAxis) {
              $candleYAxis.style.cursor = 'default';
            }
            if ($indicatorYAxis) {
              $indicatorYAxis.style.cursor = 'default';
            }

            // 设置边框样式
            if ($main) {
              $main.style.borderTop = borderLineStyle;
              $main.style[newStockInfo.data_class === 'kline' ? 'borderLeft' : 'borderRight'] =
                borderLineStyle;
            }
            if ($indicator) {
              $indicator.style[newStockInfo.data_class === 'kline' ? 'borderLeft' : 'borderRight'] =
                borderLineStyle;
            }

            // 初始tooltip展示最近日期的数据
            showKLineTooltip(kline);

            /**
             * 设置主图面板选项
             * - gap 限制主图内容的上下间隔
             * - scrollZoomEnabled 限制y轴的拖动
             */
            kline.setPaneOptions({
              id: 'candle_pane',
              gap: {
                top: 0.05,
                bottom: 0.05
              },
              axisOptions: { scrollZoomEnabled: false }
            });
          }
        }
      );

      // 创建成交量指标
      const volumeId = kline.createIndicator(
        {
          name: 'VOL',
          shouldFormatBigNumber: true,
          precision: 1
        },
        true,
        {}
      );

      // 创建一个副图技术指标VOL(清除副图的均线)
      kline.overrideIndicator(
        {
          name: 'VOL',
          shortName: '成交量',
          calcParams: [],
          precision: 2,
          figures: [
            {
              key: 'volume',
              title: '成交量: ',
              type: 'bar',
              baseValue: 0
            }
          ]
        },
        volumeId
      );

      // 设置副图tooltip高度
      const domHeight = (+$chart.offsetHeight * 1) / 4;
      kline.setPaneOptions({
        id: volumeId,
        height: Math.floor(domHeight),
        dragEnabled: false,
        gap: { top: 24, bottom: 0 },
        axisOptions: { scrollZoomEnabled: false }
      });

      /** 副图容器 */
      const indicatorDom = kline.getDom(volumeId, 'main');

      /**
       * 监听图表的十字交叉数据变化，并获取数据
       */
      kline.subscribeAction('onCrosshairChange', args => {
        const kLineData = showKLineTooltip(kline, args.dataIndex);

        /**
         * 设置副图的tooltip
         */
        function setIndicatorTooltip() {
          if (!args.indicatorData[volumeId]?.VOL) {
            return;
          }

          const { volume } = args.indicatorData[volumeId].VOL;
          const color = utils.const.HXKLINE_COLOR;
          const [black, red, green] = color;

          // 根据涨跌设置颜色
          let volumeColor;
          if (kLineData.close > kLineData.open) {
            volumeColor = red;
          } else if (kLineData.close < kLineData.open) {
            volumeColor = green;
          } else {
            volumeColor = black;
          }

          // 设置提示内容
          $indicatorTooltip.innerHTML = `<span style='color: '>成交量：</span><span style='color: ${volumeColor}'>${volume}</span>`;
          indicatorDom.insertBefore($indicatorTooltip, indicatorDom.firstChild);
        }
        setIndicatorTooltip();
      });

      return kline;
    }

    // 注册鼠标离开事件 - 清除副图提示，显示主图提示
    $chart.addEventListener('mouseleave', () => {
      $indicatorTooltip.innerHTML = '';
      if (chartIns) {
        showKLineTooltip(chartIns);
      }
    });

    // 注册股票切换事件
    scrollButtonGroupIns.on('change', e => {
      stockInfo = {
        ...generateStockInfo(),
        ...e.value
      } as Stock;
      chartIns = chartInit($chart, stockInfo);
      buttonGroupIns.init();
      showKLineTooltip(chartIns);
    });

    // 注册周期切换事件
    buttonGroupIns.on('change', e => {
      stockType = e.value;

      // 根据不同周期设置股票信息
      if (!e.key) {
        // 分时
        stockInfo = {
          id: utils.common.generateUID(),
          code: stockInfo.code,
          market: stockInfo.market,
          name: stockInfo.name,
          data_class: stockType,
          trade_class: 'intraday',
          trade_date: 0
        };
      } else {
        // K线
        stockInfo = {
          id: utils.common.generateUID(),
          code: stockInfo.code,
          market: stockInfo.market,
          name: stockInfo.name,
          data_class: 'kline',
          time_period: stockType,
          trade_date: 0,
          adjust_type: 'forward'
        };
      }

      // 重新初始化图表
      chartIns = chartInit($chart, stockInfo);
    });

    // 初始化图表
    chartIns = chartInit($chart, stockInfo);

    // 返回销毁函数
    return () => {
      // 销毁UI组件
      scrollButtonGroupIns?.destroy?.();
      buttonGroupIns?.destroy?.();
      kLineTooltipIns?.destroy?.();

      // 销毁图表
      HXKline.disposeChart($chart, (...args) => {
        console.log('hxKLine destroy', ...args);
      });

      // 清空DOM
      $dom.innerHTML = '';
    };
  }
});
